<template>
  <div :class="classes">
    <span
      class="toggle-password-btn"
      v-if="typeIsPassword"
      @click="toggleTypePassword" @keyDown="toggleTypePassword">
      <dx-icon :icon="['fas', iconPasswordBtn]" />
    </span>
    <input
      :id="id"
      :type="typeActual"
      :aria-label="label"
      :name="name"
      placeholder=" "
      :autocomplete="autocomplete"
      @blur="validateInput"
      :value="modelValue"
      @input="updateValue"
      :disabled="disabled"
      :minlength="minlength"
      :maxlength="maxlength"
      :required="required"
      />
    <span class="floating-label">
      {{ label }}
    </span>
    <p v-if="errorMessageActual">{{errorMessageActual}}</p>
  </div>
</template>

<script>
import { library } from '@fortawesome/fontawesome-svg-core';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { maskService } from '../functions';

library.add(faEye);
library.add(faEyeSlash);

export default {
  name: 'dx-input',
  props: {
    id: String,
    customValidation: Function,
    errorMessage: String,
    type: {
      type: String,
      default: 'text',
    },
    label: String,
    modelValue: {
      type: String,
      default: '',
    },
    required: Boolean,
    disabled: Boolean,
    minlength: Number,
    maxlength: Number,
    mask: {
      type: String,
      validator: (propValue) => Object.prototype.hasOwnProperty.call(maskService, propValue),
    },
    autocomplete: {
      type: String,
      default: 'off',
    },
  },
  watch: {
    errorMessage(newValue) {
      this.errorMessageActual = newValue;
    },
    disabled(newValue) {
      if (newValue) {
        this.errorMessageActualTemp = this.errorMessageActual;
        this.errorMessageActual = undefined;
      } else {
        this.errorMessageActual = this.errorMessageActualTemp;
      }
    },
  },
  data() {
    return {
      errorMessageActual: this.errorMessage,
      typeActual: this.type,
    };
  },
  computed: {
    name() {
      return this.label.replace(/\s/g, '-').toLowerCase();
    },
    classes() {
      return {
        'input-error': this.errorMessageActual !== undefined,
      };
    },
    typeIsPassword() {
      return this.type === 'password';
    },
    iconPasswordBtn() {
      return this.typeActual === 'password' ? 'eye-slash' : 'eye';
    },
  },
  mounted() {
    this.applyMask(this.modelValue);
    this.addValidationOnForm();
  },
  methods: {
    addValidationOnForm() {
      const parent = this.$parent;
      if (parent?.$?.type?.name === 'dx-form') {
        parent.validations.push(this.validateInput);
      }
    },
    async validateInput() {
      this.errorMessageActual = undefined;

      if (!this.disabled) {
        if (this.required && this.modelValue === '') {
          this.errorMessageActual = 'Este campo é obrigatório';
        } else if (this.customValidation) {
          this.errorMessageActual = await this.customValidation(this);
        }
      }

      return this.errorMessageActual === undefined;
    },
    updateValue(event) {
      const { target } = event;
      target.value = this.applyMask(target.value);
    },
    applyMask(value) {
      const maskedValue = this.mask
        ? maskService[this.mask].apply(value)
        : value;

      this.$emit('update:modelValue', maskedValue);
      return maskedValue;
    },
    toggleTypePassword() {
      this.typeActual = this.typeActual === 'password' ? 'text' : 'password';
    },
  },
};
</script>

<style scoped lang="scss">
  div {
    position: relative;
    padding: var(--dx-space-size--xs);
  }
  input {
    width: 100%;
    min-height: 40px;
    padding: var(--dx-space-size--xs) var(--dx-space-size--sm);
    border: 2px solid var(--dx-color--grey-semi-light);
    border-radius: 3px;

    font-size: var(--dx-text-size--md);
    text-decoration: none;
    white-space: nowrap;
    vertical-align: middle;
    user-select: none;
    line-height: inherit;
    outline: none !important;

    -webkit-appearance: button;

    &:focus, &:hover {
      border-color: var(--dx-color--grey);
    }
    &:-webkit-autofill,
    &:-webkit-autofill:hover,
    &:-webkit-autofill:focus,
    &:-webkit-autofill:active {
        -webkit-box-shadow: 0 0 0 30px white inset !important;
    }

    &:focus ~ .floating-label,
    &:not(:placeholder-shown) ~ .floating-label,
    &:-webkit-autofill ~ .floating-label {
      top: 2px;
      bottom: 10px;
      left: 20px;
      font-size: 11px !important;
      opacity: 1;
      height: 15px;
      background: #fff;
      color: #000;
    }
    &:-webkit-autofill ~ .floating-label {
      transition: 0.0s ease all;
    }
    &:disabled {
      background-color: var(--dx-color--grey-light);
      color:  var(--dx-color--grey-dark);
      opacity: 0.8;
      cursor: not-allowed;
    }
  }
  .floating-label {
    position: absolute;
    pointer-events: none;
    left: var(--dx-space-size--sm);
    top: calc(var(--dx-space-size--sm) + 3px);
    padding: 1px 6px;
    transition: 0.2s ease all;
    color: var(--dx-color--grey-dark);
    border-radius: var(--dx-space-size--md);

    max-width: calc(100% - var(--dx-space-size--lg));
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;

    -webkit-touch-callout: none;
    -webkit-user-select: none;
    -khtml-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
  }
  p {
    margin: 0px var(--dx-space-size--xs) calc(var(--dx-space-size--xs) * -1);

    font-size: var(--dx-text-size--sm);
    text-align: left !important;
  }
  .input-error{
    input {
      border-color: var(--dx-color--red) !important;
    }
    p {
      color: var(--dx-color--red) !important;
    }
  }
  .toggle-password-btn {
    position: absolute;
    right: var(--dx-space-size--xs);
    padding: 12px var(--dx-space-size--xs);
    color: var(--dx-color--grey);
    font-size: var(--dx-text-size--sm);

    & ~ input {
      padding-right: var(--dx-space-size--xl);
    }

    &:hover {
      opacity: 0.8;
      color: var(--dx-color--grey-semi-light);
    }
  }
  .loading * {
    pointer-events: none!important;
    cursor: wait !important;
    opacity: 0.8 !important;
  }
</style>
